"use client"

import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react"
import { ContextType, Provider } from "../context"
import { store } from "@utils/store"
import { useRouter, useSearchParams } from "next/navigation"

export function getParamsFromSearch(search: URLSearchParams) {
    return {
        time: Number(search.get("t")) || 7,
        sort: search.get("sort") || "stars:desc",
        pointsMin: Number(search.get("pocMin")) || 20,
        pointsMax: Number(search.get("pocMax")) || 1600,
        resorts: (search.get("r")?.split(",") || []).map((id) => Number(id)),
        useMonth: search.get("use")?.split(",") || [],
        withSold: search.get("s") !== "0"
    }
}
/**
url sorting naming
s=0 - only not sold when
ppp=20 - price per point
pocMin=30 - points on contract
pocMax=30 - points on contract
r=10,11,12 - resort id
use=April - use month
 */
export function DealsContext({ children }: PropsWithChildren<{}>) {
    const search = useSearchParams()

    const [state, _setState] = useState<ContextType>(() => {
        const storeValue = store.get(store.k.hotDeals)
        if (storeValue.initial && search.has("t")) {
            return {
                time: Number(search.get("t")) || 7,
                sort: search.get("sort") || "stars:desc",
                pointsMin: Number(search.get("pocMin") || storeValue.pointsMin),
                pointsMax: Number(search.get("pocMax") || storeValue.pointsMax),
                resorts: (search.get("r")?.split(",") || []).map((id) => Number(id)),
                useMonth: search.get("use")?.split(",") || [],
                withSold: search.get("s") !== "0"
            }
        }
        return storeValue
    })
    const router = useRouter()
    const asyncRedirect = useCallback(
        async (ns: ContextType) => {
            const params = {
                t: ns.time.toString(),
                sort: ns.sort === "stars:desc" ? "" : ns.sort,
                pocMin: ns.pointsMin === 20 ? "" : ns.pointsMin.toString(),
                pocMax: ns.pointsMax === 1600 ? "" : ns.pointsMax.toString(),
                use: ns.useMonth.join(","),
                r: ns.resorts.join(","),
                s: ns.withSold ? "" : "0"
            }
            Object.keys(params).forEach((key) => {
                if (params[key] === "" || (Array.isArray(params[key]) && params[key].length === 0)) {
                    delete params[key]
                }
            })
            if (location.search === `?${new URLSearchParams(params).toString()}`) return
            setTimeout(() => {
                router.push(`/hottest-deals?${new URLSearchParams(params).toString()}`, { scroll: false })
            }, 100)
        },
        [router]
    )
    const setState = useCallback(
        (s: Partial<typeof state>) => {
            _setState((old) => {
                const ns = { ...old, ...s, initial: false }
                store.set(store.k.hotDeals, ns)
                asyncRedirect(ns)
                return ns
            })
        },
        [asyncRedirect]
    )
    useEffect(() => {
        store.set(store.k.hotDeals, {
            ...store.get(store.k.hotDeals),
            page: 1
        })
    }, [])
    const value = useMemo(() => {
        return {
            ...state,
            setState
        }
    }, [state, setState])

    return <Provider value={value}>{children}</Provider>
}
